import React, { useContext, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Legend,
  TimeScale,
} from 'chart.js'

import { Bar, Doughnut } from 'react-chartjs-2'
import 'chartjs-adapter-date-fns'
import './style.scss'
import DocumentEvolutionChart from '../DocumentEvolutionChart'
import { docLabels, DocType, Status, statusColors, statusLabels } from "../../utils/utils";
import { DocumentStats } from "shared/types/types";
import {Tooltip} from "react-tooltip";
import UnauthorizedPage from "../UnauthorizedPage";
import { UserContext } from "features/account";

interface DocumentVerificationStatsProps {
  data: Record<string, DocumentStats>
}

// ---- Chart.js config ----
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Legend,
  TimeScale
)

const statusTypes: Status[] = ['lost', 'followed', 'darkWeb', 'notFound']
const docsTypes: DocType[] = ['passeport' , 'id' , 'permis' , 'sejour' , 'hc' , 'none']


const DocumentVerificationStats: React.FC<DocumentVerificationStatsProps> = ({
   data,
}) => {
  const docTypes = useMemo(() => Object.keys(data), [data])
  const { user} = useContext(UserContext);


  // nombre de requêtes par type de document
  const countsByDoc = useMemo(() => {
    return docTypes.map((docType) => {
      const stats = data[docType]
      return statusTypes.reduce((acc, status) => acc + stats[status].length, 0)
    })
  }, [data, docTypes])

  // nombre de requêtes par type de statut
  const countsByStatus = useMemo(() => {
    return statusTypes.map((status) =>
      docTypes.reduce((acc, docType) => acc + data[docType][status].length, 0)
    )
  }, [data, docTypes])

  // liste détaillée pour tableau
  const allItems = useMemo(() => {
    const items: {
      date: string
      status: Status
      docType: DocType
      docNumber: string
    }[] = []

    docTypes.forEach((docType) => {
      statusTypes.forEach((status) => {
        data[docType][status].forEach((item) => {
          items.push({
            date: new Date(item.requestDate).toISOString(),
            status,
            docType,
            docNumber: item.docNumber,
          })
        })
      })
    })

    // Tri par date décroissante
    items.sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime()
    })

    return items
  }, [data, docTypes])


  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // nombre d'éléments par page

  const totalPages = Math.ceil(allItems.length / itemsPerPage);


  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handlePageClick = (page: any) => {
    setCurrentPage(page);
  };

  // Calcul des éléments à afficher sur la page actuelle
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = allItems.slice(indexOfFirstItem, indexOfLastItem);

  if (user?.partnerRole !== 'admin' || user?.type !== "partner") {
    return (
      <UnauthorizedPage
        message="Votre compte n'est pas admin!"
        alertText=" Seul les administrateurs ont accès aux stats."
      />
    );
  }

  return (
    <div className="DocumentVerificationStats">
      <h3>Statistiques des vérifications de documents de votre entreprise</h3>

      <div className='d-flex mt-10 gap-5 align-items-center justify-content-center flex-wrap mb-3'>
        {/* Chart par type de document */}
        <div className="chart-container small">
          <h4>Répartition des requêtes par type de document</h4>
          <Bar
            data={{
              labels: docTypes.map((s) => docLabels[s as DocType]), // noms jolis,
              datasets: [
                {
                  label: "nombre de vérifications ",
                  data: countsByDoc,
                  backgroundColor: '#46bab0',
                },
              ],
            }}
            options={{ responsive: true, plugins: { legend: { position: "top", display: false } } }}
          />
        </div>

        {/* Chart par type de statut */}
        <div className="chart-container small">
          <h4>Répartition des requêtes par type de réponse</h4>
          <Doughnut
            data={{
              labels: statusTypes.map((s) => statusLabels[s as Status]), // noms jolis
              datasets: [
                {
                  label: 'Répartition',
                  data: countsByStatus,
                  backgroundColor: statusTypes.map((s) => statusColors[s as Status]),
                  borderWidth: 1,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { position: 'bottom' },
                tooltip: {
                  callbacks: {
                    label: (ctx) => {
                      const label = ctx.label || ''
                      const value = ctx.parsed || 0
                      const total = countsByStatus.reduce((a, b) => a + b, 0)
                      const percent = ((value / total) * 100).toFixed(1)
                      return `${label} : ${value} docs, soit (${percent}%) de toutes vos vérifications`
                    },
                  },
                },
              },
            }}
          />
        </div>

      </div>

      {/* Courbe temporelle */}
      <DocumentEvolutionChart data={data} />

      {/* Tableau détaillé */}
      <div className="table-container mt-2">

        <h4><span>Détails des vérifications : </span>
          <>
          <span data-tooltip-id="my-tooltip"
                data-tooltip-place="top"
                data-tooltip-content={`${allItems.length ?? ''} vérifications effectuées`}
                className={`text-md tag tag-info p-1`}>
                  {allItems.length ?? ''}
          </span>
            <Tooltip id="my-tooltip" variant="info" float={true} className="text-lowercase font-M"/>
          </>
        </h4>
        <table>
          <thead>
          <tr>
            <th>Type de document</th>
            <th>Date</th>
            <th>Statut</th>
          </tr>
          </thead>
          <tbody>
          {currentItems.map((item, i) => (
            <tr key={`${item.docType}-${item.status}-${i}`}>
              <td>{ docLabels[item.docType]}</td>
              <td>{new Date(item.date).toLocaleString()}</td>
              <td style={{ color: statusColors[item.status] }}>{ statusLabels[item.status]}</td>
            </tr>
          ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="pagination">
          {/* Première page */}
          <button
            className={currentPage === 1 ? "active" : ""}
            onClick={() => handlePageClick(1)}
          >
            1
          </button>

          {/* Ellipse avant la page courante */}
          {currentPage > 3 && <span>…</span>}

          {/* Pages autour de la page courante */}
          {[currentPage - 1, currentPage, currentPage + 1].map((page) => {
            if (page > 1 && page < totalPages) {
              return (
                <button
                  key={page}
                  className={currentPage === page ? "active" : ""}
                  onClick={() => handlePageClick(page)}
                >
                  {page}
                </button>
              );
            }
            return null;
          })}

          {/* Ellipse après la page courante */}
          {currentPage < totalPages - 2 && <span>…</span>}

          {/* Dernière page */}
          {totalPages > 1 && (
            <button
              className={currentPage === totalPages ? "active" : ""}
              onClick={() => handlePageClick(totalPages)}
            >
              {totalPages}
            </button>
          )}

          {/* Bouton Suivant */}
          <button onClick={handleNext} disabled={currentPage === totalPages}>
            Suivant
          </button>
        </div>
      </div>
    </div>
  )
}

export default DocumentVerificationStats;
